/**
 * BeginPage — the radically compressed onboarding entry point.
 *
 * Per Craig's strategic lock: the activation event is
 * *time-to-first-wall-reaction*, NOT signup completion. Every tap
 * between landing → first guest snap is friction. This page exists
 * to compress that funnel to a single screen.
 *
 * What's been deliberately CUT versus the legacy /create flow:
 *   ✗ no addon selection step (package anxiety)
 *   ✗ no date input step (defaults to today silently)
 *   ✗ no event-type step (the wall is the same for every event)
 *   ✗ no auth gate (draft events are anonymous; can be claimed later)
 *   ✗ no template / theme picker (presets handle that)
 *   ✗ no checkout step (snap upload + wall is free)
 *
 * Remaining surface: ONE input. ONE button.
 *
 * Mood: the page reads like the host is being invited to name the
 * night, not provision a SaaS product. Soft cream-on-walnut palette,
 * italic serif, generous breathing room.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAPER_BG = '#1c1108';
const PAPER_HI = '#2a190a';
const INK      = '#FFE7A8';
const INK_MUTED = 'rgba(255,235,210,0.62)';
const GOLD     = '#D4AF5E';
const HAIR     = 'rgba(212,170,82,0.32)';

export default function BeginPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Autofocus the input on mount — there's literally nothing else
  // for the user to do on this screen.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 350);
    return () => clearTimeout(t);
  }, []);

  const open = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/events/draft`, { event_name: trimmed });
      const eventId = res.data?.event_id;
      if (!eventId) throw new Error('No event_id in response');

      // Capture creation timestamp client-side so the wall-ready
      // page can show "X seconds from naming to wall alive ✦"
      try { sessionStorage.setItem(`begin_started_${eventId}`, Date.now().toString()); } catch {}

      // ──────────────────────────────────────────────────────────
      // Pending edition resume — if the visitor arrived here from
      // /pricing after pressing one of the editions, finish the
      // commission chain now (claim the draft under the authed user,
      // open a Stripe-hosted checkout, hand off). The payment gate
      // MUST NOT be skipped — the visitor only reaches the product
      // surface via /preserved/{slug} after Stripe success.
      // ──────────────────────────────────────────────────────────
      let pendingEdition = null;
      try { pendingEdition = sessionStorage.getItem('eventdrop_pending_edition'); } catch {}

      if (pendingEdition && user) {
        try {
          await axios.post(`${API}/events/claim`, { event_id: eventId });
          try { sessionStorage.setItem('eventdrop_current_event_id', eventId); } catch {}
          const ckt = await axios.post(`${API}/events/${eventId}/editions/checkout`, {
            edition_id: pendingEdition,
          });
          try { sessionStorage.removeItem('eventdrop_pending_edition'); } catch {}
          const stripeUrl = ckt?.data?.url;
          if (stripeUrl) {
            window.location.href = stripeUrl;
            return;
          }
        } catch (commissionErr) {
          // If the commission resume fails, surface a quiet error
          // and stop — DO NOT silently route into the product.
          // eslint-disable-next-line no-console
          console.warn('commission resume failed', commissionErr?.response?.data || commissionErr);
          setError("We couldn't open the commission. Try again in a moment.");
          setSubmitting(false);
          return;
        }
      }

      navigate(`/wall-ready/${eventId}`);
    } catch (err) {
      setError("Something didn't quite settle. Try again in a moment.");
      setSubmitting(false);
    }
  };

  const canSubmit = name.trim().length >= 2 && !submitting;

  return (
    <div data-testid="begin-page" style={{
      minHeight: '100vh',
      width: '100%',
      overflowX: 'hidden',
      background: `radial-gradient(ellipse at top, ${PAPER_HI} 0%, ${PAPER_BG} 60%, #050302 100%)`,
      color: INK,
      padding: 'clamp(16px, 4vw, 24px)',
      display: 'flex', flexDirection: 'column',
      boxSizing: 'border-box',
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@500&display=swap" rel="stylesheet" />
      {/* Responsive rules — at narrow viewports the input row stacks so
          the CTA never gets pushed off-screen. Editorial typography is
          preserved; we don't shrink it just to fit. */}
      <style>{`
        .begin-input-row { flex-direction: row; align-items: center; padding: 6px 6px 6px 18px; gap: 0; }
        .begin-input { min-width: 0; width: 100%; }
        .begin-cta { flex-shrink: 0; }
        @media (max-width: 520px) {
          .begin-input-row { flex-direction: column; align-items: stretch; gap: 10px; padding: 12px; }
          .begin-input { width: 100%; padding: 12px 6px !important; text-align: center; }
          .begin-cta { width: 100%; padding: 14px 16px !important; }
        }
      `}</style>

      <header style={{ paddingTop: 14 }}>
        <a href="/" style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic', fontSize: 14, color: INK_MUTED,
          textDecoration: 'none', letterSpacing: '0.04em',
        }}>← EventDrop</a>
      </header>

      <main style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '40px 16px',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.32em',
          textTransform: 'uppercase', color: GOLD, marginBottom: 22,
        }}>
          ✦ BEGIN
        </div>

        <h1 style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontWeight: 500, letterSpacing: '-0.01em',
          fontSize: 'clamp(34px, 7vw, 58px)', lineHeight: 1.05,
          margin: 0, color: INK,
        }}>
          What's the night called?
        </h1>

        <p style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic', fontSize: 'clamp(15px, 1.4vw, 17px)',
          color: INK_MUTED, lineHeight: 1.6,
          margin: '20px 0 38px',
          maxWidth: 520,
        }}>
          The wall opens as soon as it has a name. You can change it later.
        </p>

        <form onSubmit={open} style={{
          width: '100%', maxWidth: 520,
          display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        }}>
          <div className="begin-input-row" style={{
            display: 'flex',
            background: 'rgba(20,12,6,0.78)',
            border: `1px solid ${HAIR}`, borderRadius: 14,
            boxShadow: '0 18px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(212,170,82,0.10)',
          }}>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 80))}
              placeholder="Sarah & James, Year 6 Leavers…"
              disabled={submitting}
              data-testid="begin-event-name"
              autoComplete="off"
              autoCapitalize="words"
              className="begin-input"
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: INK, fontFamily: 'Georgia, "Times New Roman", serif',
                fontStyle: 'italic',
                fontSize: 'clamp(17px, 2vw, 19px)',
                padding: '14px 8px',
              }}
            />
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="begin-open-wall"
              className="begin-cta"
              style={{
                background: canSubmit
                  ? 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)'
                  : 'rgba(212,170,82,0.18)',
                color: canSubmit ? '#0F0B1A' : 'rgba(255,235,210,0.45)',
                border: 'none', borderRadius: 10,
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 700, fontSize: 14.5, letterSpacing: '0.02em',
                padding: '12px 20px', cursor: canSubmit ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
                transition: 'transform 240ms ease',
              }}
              onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {submitting ? 'Opening…' : 'Open the wall ✦'}
            </button>
          </div>
          {error && (
            <p style={{
              marginTop: 14, color: '#FFAA90',
              fontStyle: 'italic', fontSize: 13,
            }} data-testid="begin-error">{error}</p>
          )}
        </form>

        {/* Three quiet reassurance lines — restraint-mode, no
            checkmarks, no comparisons, no "free vs paid" framing. */}
        <div style={{
          marginTop: 56, display: 'flex', flexWrap: 'wrap',
          justifyContent: 'center', gap: 24,
          maxWidth: 720,
          opacity: 0.62,
        }} data-testid="begin-reassurance">
          {[
            'No account yet',
            'No card needed',
            'You can add details later',
          ].map((s) => (
            <span key={s} style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic', fontSize: 13, color: INK_MUTED,
              letterSpacing: '0.02em',
            }}>· {s}</span>
          ))}
        </div>
      </main>

      <footer style={{ textAlign: 'center', padding: '24px 0 14px' }}>
        <span style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic', fontSize: 11, color: 'rgba(212,170,82,0.5)',
          letterSpacing: '0.18em', textTransform: 'uppercase',
        }}>
          ✦ Preserved nights since 2024
        </span>
      </footer>
    </div>
  );
}
